import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  AIResponse,
  MessageSkeleton,
  UserQuestion,
} from '@/components/features/chat/components/messages';
import { logger } from '@/lib/utils/logger';
import { AdviceItem, ConversationAdvice } from '@/types';

interface ChainAndResponseProps {
  advice: AdviceItem;
  onChainComplete: () => void;
  recentPromptRef: React.RefObject<HTMLDivElement>;
  title?: string;
  isStreaming?: boolean;
  isLoading?: boolean;
}

const ChainAndResponse = React.memo(
  ({
    advice,
    onChainComplete,
    recentPromptRef,
    title,
    isStreaming,
    isLoading,
  }: ChainAndResponseProps) => {
    const [chainState, setChainState] = useState<
      'initial' | 'animating' | 'complete'
    >('initial');
    const [hasRenderedQuestion, setHasRenderedQuestion] = useState(false);
    const completionTimeoutRef = useRef<NodeJS.Timeout>();
    const isUnmountingRef = useRef(false);
    const hasAdviceRef = useRef(false);
    const lastAdviceRef = useRef<string | null>(null);

    // Track if we have received advice content
    useEffect(() => {
      if (advice.advice && !hasAdviceRef.current) {
        hasAdviceRef.current = true;
        lastAdviceRef.current = advice.advice;
      }
    }, [advice.advice, advice.id, chainState, isLoading, isStreaming]);

    // Handle question rendering
    useEffect(() => {
      if (advice.question && !hasRenderedQuestion) {
        setHasRenderedQuestion(true);
      }
    }, [
      advice.question,
      advice.id,
      hasRenderedQuestion,
      isLoading,
      isStreaming,
    ]);

    // Handle chain completion
    const completeChain = useCallback(() => {
      if (chainState !== 'complete' && !isUnmountingRef.current) {
        // Only complete if we have advice content or streaming is done
        if (hasAdviceRef.current || (!isStreaming && !isLoading)) {
          setChainState('complete');
          onChainComplete();
        }
      }
    }, [chainState, isStreaming, isLoading, onChainComplete]);

    // Setup chain completion timeout
    useEffect(() => {
      if (chainState === 'initial' && hasRenderedQuestion) {
        setChainState('animating');

        // Only start completion timeout if we have advice or aren't loading/streaming
        if (hasAdviceRef.current || (!isStreaming && !isLoading)) {
          completionTimeoutRef.current = setTimeout(completeChain, 1000);
        }
      }

      return () => {
        if (completionTimeoutRef.current) {
          clearTimeout(completionTimeoutRef.current);
          completionTimeoutRef.current = undefined;
        }
      };
    }, [
      chainState,
      completeChain,
      advice.id,
      isStreaming,
      isLoading,
      hasRenderedQuestion,
    ]);

    // Handle unmounting
    useEffect(() => {
      return () => {
        isUnmountingRef.current = true;

        if (completionTimeoutRef.current) {
          clearTimeout(completionTimeoutRef.current);
        }
      };
    }, [advice.id, chainState, hasRenderedQuestion, isLoading, isStreaming]);

    const chainVariants = React.useMemo(
      () => ({
        initial: { opacity: 0, y: 20, scale: 0.95 },
        animate: {
          opacity: 1,
          y: 0,
          scale: 1,
          transition: {
            type: 'spring',
            stiffness: 500,
            damping: 30,
            mass: 0.8,
          },
        },
        exit: { opacity: 0, y: -20, transition: { duration: 0.2 } },
      }),
      []
    );

    // Determine if we should show the response
    const shouldShowResponse =
      chainState === 'complete' &&
      (hasAdviceRef.current || lastAdviceRef.current);
    const showLoading = (isLoading || isStreaming) && !hasAdviceRef.current;

    const handleAnimationComplete = React.useCallback(() => {
      if (!hasRenderedQuestion) setHasRenderedQuestion(true);
    }, [hasRenderedQuestion]);

    return (
      <AnimatePresence mode='wait' initial={false}>
        <motion.div
          key={advice.id}
          variants={chainVariants}
          initial='initial'
          animate='animate'
          exit='exit'
          className='relative w-full space-y-4'
        >
          {/* Always render question first */}
          <motion.div
            variants={chainVariants}
            initial='initial'
            animate='animate'
            exit='exit'
            onAnimationComplete={handleAnimationComplete}
          >
            <UserQuestion
              advice={advice as unknown as ConversationAdvice}
              title={title}
              isLoading={
                chainState === 'initial' ||
                chainState === 'animating' ||
                showLoading
              }
            />
          </motion.div>

          {/* Then render response or loading state */}
          {shouldShowResponse ? (
            <motion.div variants={chainVariants} className='w-full'>
              <AIResponse
                advice={advice as unknown as ConversationAdvice}
              />
            </motion.div>
          ) : (
            <motion.div variants={chainVariants} className='w-full'>
              {showLoading && <MessageSkeleton />}
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    );
  }
);

ChainAndResponse.displayName = 'ChainAndResponse';

export { ChainAndResponse };
